import React, { useEffect, useRef, useState } from 'react'
import { Modal, Result, Spin, Typography, message } from 'antd'
import { QRCodeCanvas } from 'qrcode.react'
import { sendHttpPostRequest } from '@/api/http-request'
import { modalTheme } from './modalTheme'
import { modalCopy } from './modalCopy'

const { Text } = Typography

interface CoursePaymentModalProps {
  visible: boolean
  courseId: number
  priceCent?: number
  onSuccess: () => void
  onCancel: () => void
}

const MAX_POLL = 150

const CoursePaymentModal: React.FC<CoursePaymentModalProps> = ({
  visible,
  courseId,
  priceCent,
  onSuccess,
  onCancel,
}) => {
  const [orderNo, setOrderNo] = useState('')
  const [codeUrl, setCodeUrl] = useState('')
  const [amountCent, setAmountCent] = useState(priceCent || 9980)
  const [status, setStatus] = useState<'loading' | 'pending' | 'success' | 'error'>('loading')
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const pollCountRef = useRef(0)

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const queryOrderStatus = (targetOrderNo: string) => {
    if (!targetOrderNo) return
    sendHttpPostRequest('payment/api/query_course_order_status', { order_no: targetOrderNo }, (response: any) => {
      if (response?.is_ok && response?.data?.status == 2) {
        stopPolling()
        setStatus('success')
        setTimeout(() => {
          onSuccess()
        }, 800)
      }
    })
  }

  const startPolling = (targetOrderNo: string) => {
    stopPolling()
    pollCountRef.current = 0
    timerRef.current = setInterval(() => {
      pollCountRef.current += 1
      if (pollCountRef.current > MAX_POLL) {
        stopPolling()
        setStatus('error')
        return
      }
      queryOrderStatus(targetOrderNo)
    }, 2000)
  }

  const createOrder = () => {
    if (!courseId) {
      setStatus('error')
      return
    }

    setStatus('loading')
    sendHttpPostRequest('payment/api/create_course_order', { course_id: courseId }, (response: any) => {
      if (!response?.is_ok) {
        setStatus('error')
        message.error(response?.message || modalCopy.paymentCreateOrderFailed)
        return
      }

      if (response?.data?.already_paid) {
        setStatus('success')
        setTimeout(() => {
          onSuccess()
        }, 500)
        return
      }

      const _orderNo = response?.data?.order_no || ''
      const _codeUrl = response?.data?.code_url || ''
      const _amount = response?.data?.amount || priceCent || 9980
      setOrderNo(_orderNo)
      setCodeUrl(_codeUrl)
      setAmountCent(_amount)
      setStatus('pending')

      if (_orderNo) {
        startPolling(_orderNo)
      }
    })
  }

  useEffect(() => {
    if (visible) {
      setOrderNo('')
      setCodeUrl('')
      setStatus('loading')
      createOrder()
    } else {
      stopPolling()
    }

    return () => {
      stopPolling()
    }
  }, [visible, courseId])

  const renderBody = () => {
    if (status === 'success') {
      return <Result status="success" title={modalCopy.paymentSuccessTitle} subTitle={modalCopy.paymentSuccessSubtitle} style={{ padding: '20px 0' }} />
    }

    if (status === 'error') {
      return <Result status="warning" title={modalCopy.paymentErrorTitle} subTitle={modalCopy.paymentErrorSubtitle} style={{ padding: '20px 0' }} />
    }

    if (status === 'loading') {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: `${modalTheme.spacing3xl}px 0`,
            background: modalTheme.paymentBgGradient,
            borderRadius: modalTheme.radiusLg,
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: modalTheme.spacing2xl }}>
            <Text type="secondary">{modalCopy.paymentCreatingOrder}</Text>
          </div>
        </div>
      )
    }

    return (
      <div
        style={{
          textAlign: 'center',
          padding: `${modalTheme.spacing2xl}px 0 ${modalTheme.spacingSm}px 0`,
          background: modalTheme.paymentBgGradient,
          borderRadius: modalTheme.radiusLg,
          border: `1px solid ${modalTheme.paymentBorder}`,
        }}
      >
        <div style={{ marginBottom: modalTheme.spacingSm }}>
          <Text style={{ fontSize: modalTheme.fontXs, color: modalTheme.paymentBadgeText, background: modalTheme.paymentBadgeBg, padding: '4px 10px', borderRadius: 999 }}>
            {modalCopy.paymentChannelBadge}
          </Text>
        </div>
        <div style={{ marginBottom: modalTheme.spacingMd }}>
          <Text style={{ fontSize: modalTheme.fontSm, color: modalTheme.secondaryText }}>{modalCopy.paymentAmountLabel}</Text>
        </div>
        <div style={{ marginBottom: modalTheme.spacingXl }}>
          <Text style={{ fontSize: modalTheme.fontPrice, fontWeight: 700, color: modalTheme.amountText, lineHeight: 1 }}>¥{(amountCent / 100).toFixed(2)}</Text>
        </div>
        <div style={{ display: 'inline-block', padding: modalTheme.spacingMd, background: '#ffffff', borderRadius: modalTheme.radiusMd, boxShadow: modalTheme.shadowQr }}>
          {codeUrl ? <QRCodeCanvas value={codeUrl} size={210} level="M" /> : <Spin size="large" />}
        </div>
        <div style={{ marginTop: modalTheme.spacingXl }}>
          <Text style={{ fontSize: modalTheme.fontMd, color: modalTheme.cardText, fontWeight: 600 }}>{modalCopy.paymentScanHint}</Text>
        </div>
        <div style={{ marginTop: modalTheme.spacingXs }}>
          <Text type="secondary" style={{ fontSize: modalTheme.fontXs }}>
            {modalCopy.paymentAutoRefreshHint}
          </Text>
        </div>
        {orderNo && (
          <div
            style={{
              marginTop: modalTheme.spacingLg,
              padding: `${modalTheme.spacingSm}px ${modalTheme.spacingLg}px`,
              background: modalTheme.contactCardBg,
              borderTop: `1px dashed ${modalTheme.paymentBorder}`,
              borderBottomLeftRadius: modalTheme.radiusLg,
              borderBottomRightRadius: modalTheme.radiusLg,
            }}
          >
            <Text type="secondary" style={{ fontSize: modalTheme.fontXs }}>
              {modalCopy.paymentOrderPrefix}{orderNo}
            </Text>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title={modalCopy.paymentTitle}
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={modalTheme.modalWidthPayment}
      centered
    >
      {renderBody()}
    </Modal>
  )
}

export default CoursePaymentModal
